import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchReviewsByToken, submitReviewByToken } from '../services/medusa/reviews';
import SEO from '../components/SEO';
import './ReviewByToken.css';

function StarPicker({ value, onChange, disabled }) {
  return (
    <div className="rvw-stars" role="radiogroup" aria-label="Calificación">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`rvw-star ${n <= value ? 'is-on' : ''}`}
          onClick={() => !disabled && onChange(n)}
          aria-label={`${n} de 5 estrellas`}
          aria-pressed={n === value}
          disabled={disabled}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export default function ReviewByToken() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  // Estado por producto: { [product_id]: { rating, title, content, status, error } }
  const [forms, setForms] = useState({});
  // Cupón de agradecimiento devuelto por el backend al enviar la primera reseña.
  const [reward, setReward] = useState(null); // { code, label }

  useEffect(() => {
    let alive = true;
    fetchReviewsByToken(token)
      .then((data) => { if (alive) setState({ loading: false, error: '', data }); })
      .catch((err) => { if (alive) setState({ loading: false, error: err.message || 'Enlace inválido o expirado.', data: null }); });
    return () => { alive = false; };
  }, [token]);

  const setForm = (pid, patch) =>
    setForms((f) => ({ ...f, [pid]: { rating: 0, title: '', content: '', status: 'idle', error: '', ...f[pid], ...patch } }));

  const submit = async (pid) => {
    const form = forms[pid] || {};
    if (!form.rating || !(form.content || '').trim()) {
      setForm(pid, { error: 'Agrega una calificación y un comentario.' });
      return;
    }
    setForm(pid, { status: 'saving', error: '' });
    try {
      const resp = await submitReviewByToken({
        token,
        product_id: pid,
        rating: form.rating,
        title: form.title,
        content: form.content,
      });
      if (resp?.reward_code) {
        setReward({ code: resp.reward_code, label: resp.discount_label || '15%' });
      }
      setForm(pid, { status: 'done', error: '' });
    } catch (err) {
      setForm(pid, { status: err.status === 409 ? 'done' : 'idle', error: err.status === 409 ? '' : (err.message || 'No se pudo enviar.') });
    }
  };

  if (state.loading) {
    return <div className="rvw-page"><div className="rvw-inner"><p className="rvw-muted">Cargando…</p></div></div>;
  }

  if (state.error) {
    return (
      <div className="rvw-page">
        <SEO title="Reseña" path="/review" noindex />
        <div className="rvw-inner rvw-center">
          <h1 className="rvw-title">Enlace no válido</h1>
          <p className="rvw-muted">{state.error}</p>
          <p className="rvw-muted">Si el enlace expiró, puedes dejar tu reseña desde la página del producto iniciando sesión.</p>
        </div>
      </div>
    );
  }

  const { customer_name, products } = state.data;
  const pending = products.filter((p) => !p.reviewed);
  const allDone = pending.length > 0 && pending.every((p) => forms[p.product_id]?.status === 'done');

  return (
    <div className="rvw-page">
      <SEO title="Deja tu reseña" path="/review" noindex />
      <div className="rvw-inner">
        <header className="rvw-header">
          <p className="rvw-eyebrow">Physiology</p>
          <h1 className="rvw-title">{customer_name ? `${customer_name}, cuéntanos tu experiencia` : 'Cuéntanos tu experiencia'}</h1>
          <p className="rvw-muted">Tu opinión ayuda a otras personas a elegir mejor. Al enviar tu reseña, obtienes un descuento para tu próximo pedido.</p>
        </header>

        {products.length === 0 && <p className="rvw-muted">No hay productos para reseñar en este pedido.</p>}

        {allDone && (
          <div className="rvw-thanks">
            <h2 className="rvw-thanks-title">¡Gracias por tus reseñas! 🌿</h2>
            {reward ? (
              <>
                <p className="rvw-muted">Aquí tienes tu <strong>{reward.label} de descuento</strong> para tu próximo pedido (también te lo enviamos por correo):</p>
                <div className="rvw-code">{reward.code}</div>
                <p className="rvw-muted">Tus reseñas se publicarán tras una breve revisión.</p>
              </>
            ) : (
              <p className="rvw-muted">Te enviaremos por correo tu <strong>descuento</strong> para el próximo pedido. Tus reseñas se publicarán tras una breve revisión.</p>
            )}
          </div>
        )}

        <ul className="rvw-list">
          {products.map((p) => {
            const form = forms[p.product_id] || {};
            const done = p.reviewed || form.status === 'done';
            return (
              <li key={p.product_id} className={`rvw-item ${done ? 'is-done' : ''}`}>
                <div className="rvw-item-media">
                  {p.thumbnail ? <img src={p.thumbnail} alt={p.title} /> : <div className="rvw-item-ph" />}
                </div>
                <div className="rvw-item-body">
                  <h3 className="rvw-item-title">{p.title}</h3>

                  {done ? (
                    <p className="rvw-done-msg">✓ {p.reviewed ? 'Ya tienes una reseña de este producto.' : '¡Reseña enviada!'}</p>
                  ) : (
                    <>
                      <StarPicker
                        value={form.rating || 0}
                        onChange={(r) => setForm(p.product_id, { rating: r })}
                        disabled={form.status === 'saving'}
                      />
                      <input
                        type="text"
                        className="rvw-input"
                        placeholder="Título (opcional)"
                        value={form.title || ''}
                        onChange={(e) => setForm(p.product_id, { title: e.target.value })}
                        disabled={form.status === 'saving'}
                        maxLength={80}
                      />
                      <textarea
                        className="rvw-textarea"
                        placeholder="¿Qué te pareció? ¿Cómo dejó tu piel?"
                        value={form.content || ''}
                        onChange={(e) => setForm(p.product_id, { content: e.target.value })}
                        disabled={form.status === 'saving'}
                        rows={3}
                        maxLength={1000}
                      />
                      {form.error && <p className="rvw-error">{form.error}</p>}
                      <button
                        type="button"
                        className="rvw-submit"
                        onClick={() => submit(p.product_id)}
                        disabled={form.status === 'saving'}
                      >
                        {form.status === 'saving' ? 'Enviando…' : 'Enviar reseña'}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
